import { OAUTH_CONSENT_PATH, roleAtLeast } from '@inou/shared';
import { type ReactElement, StrictMode, useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';

import { AppLayout } from './AppLayout';
import { HqApp } from './hq/HqApp';
import { ApiError, api, type Me, type Operator } from './lib/api';
import { type AppRoute, canOpen, ROUTES } from './lib/nav';
import { SignupOpenContext } from './lib/signup';
import { AcceptInvitationPage } from './routes/AcceptInvitationPage';
import { AssignmentsPage } from './routes/AssignmentsPage';
import { AuditLogsPage } from './routes/AuditLogsPage';
import { CapitalizationPage } from './routes/CapitalizationPage';
import { HelpArticlePage } from './routes/help/HelpArticlePage';
import { HelpIndexPage } from './routes/help/HelpIndexPage';
import { LandingPage } from './routes/LandingPage';
import { LoginPage } from './routes/LoginPage';
import { CommercePage } from './routes/legal/CommercePage';
import { PrivacyPage } from './routes/legal/PrivacyPage';
import { TermsPage } from './routes/legal/TermsPage';
import { McpSettingsPage } from './routes/McpSettingsPage';
import { MembersPage } from './routes/MembersPage';
import { MfaEnrollmentPage } from './routes/MfaEnrollmentPage';
import { OAuthConsentPage } from './routes/OAuthConsentPage';
import { ProjectsPage } from './routes/ProjectsPage';
import { SecurityPage } from './routes/SecurityPage';
import { SignupPage } from './routes/SignupPage';
import { SignupVerifyPage } from './routes/SignupVerifyPage';
import { StartPage } from './routes/StartPage';
import { TenantSettingsPage } from './routes/TenantSettingsPage';
import { TimeLogsPage } from './routes/TimeLogsPage';
import { UtilizationPage } from './routes/UtilizationPage';
import './index.css';

/** 遷移後、先頭に押さえておく時間 (ms)。レイアウトが落ち着くまでの分だけ */
const SCROLL_SETTLE_MS = 500;

/**
 * 遷移を「ページ遷移」として扱う。
 *
 * `BrowserRouter` はスクロール位置もフォーカスも持ち越すので、長い一覧や
 * ヘルプの索引の下の方からリンクを踏むと、次の画面がその位置で開く。
 * React Router の `<ScrollRestoration>` はデータルーター専用で、
 * この構成では効かない。
 *
 * **フォーカスを捨ててからスクロールする。** 押したリンクにフォーカスが
 * 残っていると、スクロールした直後にブラウザがそのリンクを見える位置まで
 * 引き戻す (ヘルプの記事ナビのように、遷移してもリンクが画面に残る場所で
 * 起きる)。本物のページ遷移でもフォーカスは文書に戻るので、それに合わせる。
 *
 * **ハッシュ付きでは先頭に戻さない。** LP 内の節へのリンク (`/#pricing`) が
 * これに当たる。着地の時点ではまだ `/api/auth/me` を待っていて節が描画されて
 * いないことがあるので、ここでは対象があれば送るだけにして、無ければ
 * 何もしない (節を持つ画面が自分で送る。`LandingPage`)。
 *
 * **先頭には「押さえ続ける」。** 1 回 `scrollTo` するだけでは 0 に着き切らない。
 * 遷移直後はレイアウトがまだ動いていて数 px ずれ、0 から外れた時点で
 * ブラウザの scroll anchoring が効いて数十 px 下へ流される (0 ちょうどなら
 * anchoring は働かない)。落ち着くまでの短い間だけ押さえ、その間に利用者が
 * 自分でスクロールしたら即座にやめる。
 */
function ResetOnNavigate() {
  const { pathname, hash } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname は「余分な依存」ではなく遷移の検知そのもの。外すと初回しか動かない
  useEffect(() => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();

    if (hash) {
      document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView();
      return;
    }

    let stopped = false;
    let frame = 0;
    const release = () => {
      stopped = true;
    };

    // 利用者が自分でスクロールし始めたら手を引く
    window.addEventListener('wheel', release, { passive: true, once: true });
    window.addEventListener('touchstart', release, { passive: true, once: true });
    window.addEventListener('keydown', release, { once: true });

    const until = performance.now() + SCROLL_SETTLE_MS;
    const pin = () => {
      if (stopped) return;
      window.scrollTo(0, 0);
      if (performance.now() < until) frame = requestAnimationFrame(pin);
    };
    pin();

    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      window.removeEventListener('wheel', release);
      window.removeEventListener('touchstart', release);
      window.removeEventListener('keydown', release);
    };
  }, [pathname, hash]);

  return null;
}

/** クエリ文字列のトークンを読む */
function queryToken(): string | null {
  return new URLSearchParams(window.location.search).get('token');
}

/** 招待の受諾リンク (/invitation?token=...) からトークンを読む */
function invitationToken(): string | null {
  if (window.location.pathname !== '/invitation') return null;
  return new URLSearchParams(window.location.search).get('token');
}

/** サインアップの確認リンク (/signup/verify?token=...) の着地点に居るか */
function onVerifyPath(): boolean {
  return window.location.pathname === '/signup/verify';
}

/**
 * 法務ページ。
 *
 * 未ログインの分岐は `*` を /login へ送るので、両方の Routes に置かないと
 * 「契約前の人が規約を読めない」状態になる。1 箇所にまとめて両方から使う。
 */
function legalRoutes() {
  return (
    <>
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/legal/commerce" element={<CommercePage />} />
    </>
  );
}

/**
 * ヘルプ。
 *
 * 法務ページと同じく、未ログインとログイン後の両方の Routes に置く。
 * 片方だけだと、未ログイン側は `*` が /login へ、ログイン後は `*` が home へ
 * 攫っていくので、どちらも「ヘルプが開けない」になる。
 *
 * @param signedIn フッターの導線とヘッダーの戻り先を切り替える
 * @param home ログイン済みの戻り先。ロールで違うので呼び出し側から渡す
 *   (リダイレクト先は 1 箇所にまとめる)。未ログインでは使わない
 */
function helpRoutes(signedIn: boolean, home = '/') {
  return (
    <>
      <Route path="/help" element={<HelpIndexPage signedIn={signedIn} home={home} />} />
      <Route path="/help/:slug" element={<HelpArticlePage signedIn={signedIn} home={home} />} />
    </>
  );
}

/**
 * 起動時の分岐。
 *
 * 同じバンドルをアプリのホストと運営ホスト (hq.*) の両方に配信している。
 * どちらに居るかは /api/auth/me の応答で判別できる:
 *   200 -> ログイン済み
 *   401 -> アプリのホストだが未ログイン
 *   404 -> 運営ホスト (/api は運営ホスト宛だと閉じている)
 * 運営ホストなら Cloudflare Access が前段に居るので、/hq/me が通れば運営者。
 */
function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [operator, setOperator] = useState<Operator | null>(null);
  const [checked, setChecked] = useState(false);
  const [token, setToken] = useState(invitationToken);
  const [verifying, setVerifying] = useState(onVerifyPath);
  const [signupOpen, setSignupOpen] = useState<boolean | null>(null);
  /** 開通した直後か。me が載ってから「はじめに」へ送るために持つ */
  const [justSignedUp, setJustSignedUp] = useState(false);
  const navigate = useNavigate();

  /*
   * `checked` は**初回だけ**の関門で、再取得では false に戻さない。
   *
   * ここで戻すと「読み込み中…」が Routes ごと差し替え、開いていた画面が
   * unmount される。画面の state に一度きりの平文を抱えている場所
   * (TOTP のシークレット、リカバリーコード) はそれで消えるので、
   * 「設定を始める」がエラーも出さずに元の画面へ戻ってしまう。
   * ローディング表示の条件は loading ではなく loading && data === null。
   */
  const refresh = useCallback(() => {
    api
      .get<Me>('/api/auth/me')
      .then((result) => {
        setMe(result);
        setOperator(null);
        setChecked(true);
      })
      .catch(async (cause: unknown) => {
        setMe(null);
        if (cause instanceof ApiError && cause.status === 404) {
          const found = await api
            .get<{ operator: Operator }>('/hq/me')
            .then((result) => result.operator)
            .catch(() => null);
          setOperator(found);
        } else {
          setOperator(null);
        }
        setChecked(true);
      });
  }, []);

  useEffect(refresh, [refresh]);

  /*
   * セルフサインアップが開いているかは env が決めるので、起動時に 1 度だけ聞く。
   * 閉じているときは経路ごと無いので 404 になる (`/api/auth/me` の 404 で
   * 運営ホストを見分けているのと同じ形)。/api/auth/me と並行に投げる。
   */
  useEffect(() => {
    api
      .get('/api/signup')
      .then(() => setSignupOpen(true))
      .catch(() => setSignupOpen(false));
  }, []);

  /*
   * 開通した人だけは home ではなく「はじめに」へ着地させる。
   *
   * リダイレクト先を home に一本化する規約は「そのロールで開けない画面へ
   * 送らない」ためのもので、`/start` の最低ロールは member。開通した人は
   * 必ずそのテナントの admin なので、ここでループは起きない。
   *
   * `history.replaceState` では送れない (popstate が飛ばないので React Router に
   * 伝わらない)。me が載った後の遷移として扱う。
   */
  useEffect(() => {
    if (!justSignedUp || me === null) return;
    setJustSignedUp(false);
    navigate(ROUTES.start.path, { replace: true });
  }, [justSignedUp, me, navigate]);

  /*
   * 両方が揃うまで何も描かない。片方だけで描くと、申し込み導線が
   * 一瞬出てから消える (逆も同じ)。
   */
  if (!checked || signupOpen === null) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-ink-subtle">
        読み込み中…
      </div>
    );
  }

  /**
   * 申し込み導線を出すかどうかを配る。LP・フッター・法務ページが
   * それぞれ判断できないため (`LegalLayout` はログイン状態すら持たない)。
   */
  const withSignupOpen = (node: ReactElement) => (
    <SignupOpenContext.Provider value={signupOpen}>{node}</SignupOpenContext.Provider>
  );

  if (operator) {
    return <HqApp operator={operator} />;
  }

  /*
   * サインアップの確認リンクは Routes の**外側**で扱う。
   *
   * ログイン中でも開通できる必要がある (別の組織を立てることがあるので
   * /signup はログイン後も開けてあり、トライアル切れでも /api/signup は通す)。
   * ログイン後の Routes に置くと、どの Route にも一致せず AppLayout の
   * wildcard で「前のテナントの home」へ飛び、POST が発火しないまま
   * 成功したように見えてしまう。
   *
   * 開通したら URL からトークンを外して読み直す。着地先は上の useEffect が
   * me の到着を待って「はじめに」へ送る (開通した人だけの例外。理由はそこに)。
   *
   * **閉じている間はこの分岐ごと使わない。** POST しても 404 なので、
   * ここを通すと「開通できませんでした」という行き止まりに着地させるだけ。
   * 下の Routes に落ちて、未ログインなら /login へ送られる。
   */
  if (verifying && signupOpen) {
    return (
      <SignupVerifyPage
        token={queryToken()}
        onSignedIn={() => {
          window.history.replaceState(null, '', '/login');
          setVerifying(false);
          setJustSignedUp(true);
          refresh();
        }}
      />
    );
  }

  // 招待の受諾は未ログインで開く。受諾するとそのままログイン済みになるので、
  // URL からトークンを外してからアプリ本体へ入る。
  if (!me && token) {
    return (
      <AcceptInvitationPage
        token={token}
        onAccepted={() => {
          window.history.replaceState(null, '', '/');
          setToken(null);
          refresh();
        }}
      />
    );
  }

  // 未ログイン。LP と、そこからのログインだけを開ける。
  if (!me) {
    return withSignupOpen(
      <Routes>
        <Route path="/" element={<LandingPage signedIn={false} />} />
        <Route path="/login" element={<LoginPage onSignedIn={refresh} />} />
        {/* 閉じている間は経路自体を置かない (`*` が /login へ送る) */}
        {signupOpen ? <Route path="/signup" element={<SignupPage />} /> : null}
        {/*
         * OAuth の同意画面にログインせずに着いたときは、**同じ URL のまま**
         * ログイン画面を出す。/login へ送ると認可のパラメータ (client_id・
         * PKCE・state) が消え、ログインし終えた人が「何を許可するのか
         * 分からない画面」に着地する。ここなら通ったその場で同意画面に
         * 入れ替わる。
         */}
        <Route path={OAUTH_CONSENT_PATH} element={<LoginPage onSignedIn={refresh} />} />
        {legalRoutes()}
        {helpRoutes(false)}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>,
    );
  }

  /*
   * 二段階認証が必須のテナントで、このセッションが第 2 要素を通していない。
   * 業務 API は全部 403 になるので、Routes を出す前に登録画面へ送る
   * (この画面が叩くのは関門の外にある /api/auth/* だけ)。
   */
  if (me.mfaRequired && !me.mfaSatisfied) {
    return <MfaEnrollmentPage me={me} onDone={refresh} />;
  }

  const role = me.user.role;
  const isAdmin = roleAtLeast(role, 'admin');
  /**
   * ロールごとに「必ず開ける画面」が違う。
   * ここを /utilization 直書きのままにすると、その画面自体を閉じた時点で
   * mcp がリダイレクトのループに落ちる。
   */
  const home = canOpen(role, ROUTES.utilization)
    ? ROUTES.utilization.path
    : ROUTES.mcpSettings.path;
  /**
   * 権限の足りない経路は、そのロールで必ず開ける画面へ戻す。
   * 経路ごとの最低ロールは `lib/nav.ts` の表 1 箇所
   * (サイドバーと画面間のリンクも同じ表を見る)。
   */
  const guard = (route: AppRoute, element: ReactElement) =>
    canOpen(role, route) ? element : <Navigate to={home} replace />;

  return withSignupOpen(
    <Routes>
      {/* LP はログイン後も開ける。中に入る導線だけが変わる。 */}
      <Route path="/" element={<LandingPage signedIn />} />
      <Route path="/login" element={<Navigate to={home} replace />} />
      {/* 別のテナントを開通させることがあるので、ログイン済みでも開ける */}
      {signupOpen ? <Route path="/signup" element={<SignupPage />} /> : null}
      {/* 法務ページとヘルプはログイン後も同じものを出す (AppLayout の外) */}
      {legalRoutes()}
      {helpRoutes(true, home)}
      {/*
       * OAuth の同意画面。**全ロールが開ける** (MCP 接続は mcp プランの
       * 中心そのもの)。AppLayout の外に出すのは、認可の途中でサイドバーから
       * 別の画面へ逸れられるようにする理由が無いため。
       */}
      <Route path={OAUTH_CONSENT_PATH} element={<OAuthConsentPage me={me} />} />

      <Route element={<AppLayout me={me} onSignedOut={refresh} />}>
        <Route path={ROUTES.start.path} element={guard(ROUTES.start, <StartPage />)} />
        <Route
          path={ROUTES.utilization.path}
          element={guard(ROUTES.utilization, <UtilizationPage />)}
        />
        <Route
          path={ROUTES.capitalization.path}
          element={guard(ROUTES.capitalization, <CapitalizationPage />)}
        />
        <Route
          path={ROUTES.assignments.path}
          element={guard(ROUTES.assignments, <AssignmentsPage isAdmin={isAdmin} />)}
        />
        <Route
          path={ROUTES.timeLogs.path}
          element={guard(ROUTES.timeLogs, <TimeLogsPage me={me} />)}
        />
        <Route
          path={ROUTES.projects.path}
          element={guard(ROUTES.projects, <ProjectsPage isAdmin={isAdmin} />)}
        />
        <Route path={ROUTES.members.path} element={guard(ROUTES.members, <MembersPage />)} />
        <Route
          path={ROUTES.tenantSettings.path}
          element={guard(ROUTES.tenantSettings, <TenantSettingsPage isAdmin={isAdmin} />)}
        />
        <Route path={ROUTES.auditLogs.path} element={guard(ROUTES.auditLogs, <AuditLogsPage />)} />
        <Route path={ROUTES.mcpSettings.path} element={<McpSettingsPage />} />
        <Route path={ROUTES.account.path} element={<SecurityPage me={me} onChanged={refresh} />} />
        <Route path="*" element={<Navigate to={home} replace />} />
      </Route>
    </Routes>,
  );
}

const container = document.getElementById('root');
if (!container) throw new Error('#root が見つかりません');

createRoot(container).render(
  <StrictMode>
    <BrowserRouter>
      <ResetOnNavigate />
      <App />
    </BrowserRouter>
  </StrictMode>,
);
